import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, message, Select, Spin, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import TimeRangeFilter, { TimeRange } from '../../../components/dashboard/TimeRangeFilter';
import { ScaleQueryType } from '../../../graphql/types/dashboard/ScaleDashboardTypes';
import { DASHBOARD_PACIFIC_TZ } from '../../../utils/dashboardPacificTime';
import {
    DASHBOARD_EXCLUDE_HUB_LABELS,
    useOperableHubSelectOptions,
} from '../../../hooks/useOperableHubSelectOptions';
import { QUERY_DELIVERY_UNDELIVERED_ATTRIBUTION } from '../../../graphql/queries/dashboard/queryDeliveryUndeliveredAttribution';
import {
    DeliveryUndeliveredAttributionItem,
    DeliveryUndeliveredAttributionQueryInput,
    QueryDeliveryUndeliveredAttributionResponse,
} from '../../../graphql/types/dashboard/DeliveryUndeliveredAttributionTypes';
import {
    GENERATE_DASHBOARD_TABLE_EXPORT,
    GenerateDashboardTableExportResponse,
    GenerateDashboardTableExportVariables,
} from '../../../graphql/mutations/dashboard/generateDashboardTableExport';
import AsyncReportDownloadModal, {
    dashboardAsyncReportLabelI18nKeys,
    useAsyncReportExportCooldown,
} from '../../../components/asyncReportDownload';
import { showError } from '../../../utils/exceptionUtils';

type CategoryKey = 'driver' | 'warehouse' | 'handover' | 'return';

interface SubCauseDef {
    field: keyof DeliveryUndeliveredAttributionItem;
    label: string;
}

const CATEGORY_STYLE: Record<CategoryKey, { color: string; bgColor: string; label: string }> = {
    driver: { color: '#3B5FE5', bgColor: '#EEF1FE', label: '司机侧' },
    warehouse: { color: '#7C5CE0', bgColor: '#F1ECFB', label: '仓库侧' },
    handover: { color: '#E88A2A', bgColor: '#FDF3E7', label: '交接差异' },
    return: { color: '#8A8F99', bgColor: '#F1F2F4', label: '退件' },
};

const DRIVER_CAUSES: SubCauseDef[] = [
    { field: 'outForDeliveryNodeCount', label: '待派送/派送中' },
    { field: 'failedNotReturnedNodeCount', label: '派送失败未返仓' },
    { field: 'lostDamagedDriverCount', label: '丢失破损' },
];

// 「司机错扫未处理」= returnWrongUnscanNodeCount，从司机侧移到仓库侧首位；
// 「其他」是数仓侧与 warehouseSideCount 并列的第 5 归因桶，产品口径挂到仓库侧末位；
// returnWrongScanCount(原「错扫(有领取)」) 按新口径不再展示
const WAREHOUSE_CAUSES: SubCauseDef[] = [
    { field: 'returnWrongUnscanNodeCount', label: '司机错扫未处理' },
    { field: 'notPickedUpCount', label: '仓内未领取' },
    { field: 'returnNotPutAwayCount', label: '返仓未上架' },
    { field: 'returnOnPutAwayCount', label: '返仓上架中' },
    { field: 'returnUnPutAwayCount', label: '返仓已下架' },
    { field: 'lostDamagedWhsCount', label: '丢失破损' },
    { field: 'otherCount', label: '其他' },
];

const HANDOVER_CAUSES: SubCauseDef[] = [
    { field: 'handoverDiffNodeCount', label: '疑似交接差异' },
];

const RETURN_CAUSES: SubCauseDef[] = [
    { field: 'returnPkgCount', label: '退件包裹' },
];

const CATEGORY_TOP_COUNT: Record<CategoryKey, keyof DeliveryUndeliveredAttributionItem> = {
    driver: 'driverSideCount',
    warehouse: 'warehouseSideCount',
    handover: 'handoverDiffCount',
    return: 'returnCount',
};

const ATTRIBUTION_LOGIC_TEXT =
    '已领取 → 入库/留仓中 → 有上架动作 → 「司机错扫/丢失破损/返仓上架/下架」;无上架 → 「退件/丢失/其他/返仓未上架」。未领取 → 错分/错扫/交接差异/仓内。派送中 → 待派送/派送失败未返仓。';

const formatNumber = (n: number): string => Number(n).toLocaleString('en-US');

const formatPercent = (num: number, den: number): string => {
    if (!den || den <= 0) return '0%';
    const pct = (num / den) * 100;
    return `${Math.round(pct)}%`;
};

const num = (row: DeliveryUndeliveredAttributionItem, field: keyof DeliveryUndeliveredAttributionItem): number => {
    const v = row[field];
    return typeof v === 'number' ? v : Number(v) || 0;
};

// 未妥投总数 = 4 大类之和（不含 other）；数仓 undeliveredCount 存在算子覆盖缺口（ATL-AUG 实测 16→2），
// 前端按 driver+warehouse+handover+return 汇总，warehouseSideCount 已含 other，不再单独加
const attributionTotal = (row: DeliveryUndeliveredAttributionItem): number =>
    num(row, 'driverSideCount')
    + num(row, 'warehouseSideCount')
    + num(row, 'handoverDiffCount')
    + num(row, 'returnCount');

const NumberCell: React.FC<{ value: number; total: number }> = ({ value, total }) => {
    if (!value) {
        return <span style={{ color: '#BFBFBF' }}>—</span>;
    }
    return (
        <span>
            <span style={{ marginRight: 6 }}>{formatNumber(value)}</span>
            <span style={{ color: '#8C8C8C' }}>{formatPercent(value, total)}</span>
        </span>
    );
};

const DistributionBar: React.FC<{ row: DeliveryUndeliveredAttributionItem }> = ({ row }) => {
    const driver = num(row, 'driverSideCount');
    // warehouseSideCount 数仓侧已含 other，不再前端再加一次
    const warehouse = num(row, 'warehouseSideCount');
    const handover = num(row, 'handoverDiffCount');
    const returned = num(row, 'returnCount');
    const sum = driver + warehouse + handover + returned;
    if (sum <= 0) return <span style={{ color: '#BFBFBF' }}>—</span>;
    const segs: Array<{ key: CategoryKey; value: number }> = [
        { key: 'driver', value: driver },
        { key: 'warehouse', value: warehouse },
        { key: 'handover', value: handover },
        { key: 'return', value: returned },
    ];
    return (
        <Tooltip
            title={
                <div style={{ minWidth: 140 }}>
                    {segs.map((s) => (
                        <div key={s.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                            <span>
                                <span
                                    style={{
                                        display: 'inline-block', width: 8, height: 8, borderRadius: 2,
                                        backgroundColor: CATEGORY_STYLE[s.key].color, marginRight: 6,
                                    }}
                                />
                                {CATEGORY_STYLE[s.key].label}
                            </span>
                            <span>{formatNumber(s.value)} · {formatPercent(s.value, sum)}</span>
                        </div>
                    ))}
                </div>
            }
        >
            <div style={{ display: 'flex', width: 120, height: 10, borderRadius: 5, overflow: 'hidden' }}>
                {segs.map((s) =>
                    s.value > 0 ? (
                        <div
                            key={s.key}
                            style={{
                                width: `${(s.value / sum) * 100}%`,
                                backgroundColor: CATEGORY_STYLE[s.key].color,
                            }}
                        />
                    ) : null,
                )}
            </div>
        </Tooltip>
    );
};

const UndeliveredAttributionPage: React.FC = () => {
    const navigate = useNavigate();
    const [timeRange, setTimeRange] = useState<TimeRange | null>(null);
    const [selectedHubId, setSelectedHubId] = useState<number>(0);
    const [selectedWarehouseId, setSelectedWarehouseId] = useState<number>(0);
    const [downloadCompleteModalVisible, setDownloadCompleteModalVisible] = useState(false);
    const [generatedFileName, setGeneratedFileName] = useState('');
    const { exportLoading, runExportWithCooldown, getExportButtonLabel } = useAsyncReportExportCooldown();

    const { hubOptions, loading: hubsLoading } = useOperableHubSelectOptions({
        excludeLabels: [...DASHBOARD_EXCLUDE_HUB_LABELS],
        allOptionLabel: '全部',
        showAllOption: 'auto',
    });

    const hubIdToLabel = useMemo(() => {
        const map = new Map<number, string>();
        hubOptions.forEach((opt) => {
            if (opt.value !== 0) map.set(Number(opt.value), opt.label);
        });
        return map;
    }, [hubOptions]);

    const { data, loading } = useQuery<QueryDeliveryUndeliveredAttributionResponse>(
        QUERY_DELIVERY_UNDELIVERED_ATTRIBUTION,
        {
            variables: {
                input: {
                    startDate: timeRange?.startDate,
                    endDate: timeRange?.endDate,
                    hubId: selectedHubId,
                } as DeliveryUndeliveredAttributionQueryInput,
            },
            skip: !timeRange?.startDate || !timeRange?.endDate,
            onError: (err) => {
                console.error('查询未妥投归因失败:', err);
                message.error('查询未妥投归因失败,请稍后重试');
            },
        },
    );

    // 数仓宽表同时包含 (0,0) 全美汇总、(hubX,0) hub 汇总和 (hubX,whsY) 真实仓库三种行。
    // 「WHS 维度」表和顶部 categoryCards 合计只应该基于真实仓库，否则会被三种行加三遍
    const items = useMemo<DeliveryUndeliveredAttributionItem[]>(
        () => (data?.queryDeliveryUndeliveredAttribution ?? [])
            .filter((r) => Number(r.warehouseId) !== 0),
        [data],
    );

    // 派送站候选：跟 daily-meeting 一样从当前查询结果去重，hub 联动天然成立
    const warehouseOptions = useMemo(() => {
        const seen = new Set<number>();
        const opts: { value: number; label: string }[] = [];
        items.forEach((r) => {
            const wid = Number(r.warehouseId);
            if (!wid || seen.has(wid)) return;
            seen.add(wid);
            opts.push({ value: wid, label: r.sortingCode ?? `Warehouse#${wid}` });
        });
        opts.sort((a, b) => a.label.localeCompare(b.label));
        return [{ value: 0, label: '全部' }, ...opts];
    }, [items]);

    // 换 hub 后原选中派送站可能不在候选里，回退到「全部」
    useEffect(() => {
        if (selectedWarehouseId === 0) return;
        const stillAvailable = items.some((r) => Number(r.warehouseId) === selectedWarehouseId);
        if (!stillAvailable) setSelectedWarehouseId(0);
    }, [items, selectedWarehouseId]);

    const filteredItems = useMemo(
        () => (selectedWarehouseId === 0
            ? items
            : items.filter((r) => Number(r.warehouseId) === selectedWarehouseId)),
        [items, selectedWarehouseId],
    );

    const sortedRows = useMemo(
        () => [...filteredItems].sort((a, b) => attributionTotal(b) - attributionTotal(a)),
        [filteredItems],
    );

    const totalUndelivered = useMemo(
        () => filteredItems.reduce((s, r) => s + attributionTotal(r), 0),
        [filteredItems],
    );

    const categoryCards = useMemo(() => {
        const buildCard = (key: CategoryKey, subs: SubCauseDef[]) => {
            // warehouseSideCount 数仓侧已含 other（other 只作为仓库侧内的一个子类展示），不再叠加
            const total = filteredItems.reduce((s, r) => s + num(r, CATEGORY_TOP_COUNT[key]), 0);
            return {
                key,
                label: CATEGORY_STYLE[key].label,
                color: CATEGORY_STYLE[key].color,
                bgColor: CATEGORY_STYLE[key].bgColor,
                total,
                ratioText: formatPercent(total, totalUndelivered),
                subs: subs.map((c) => ({
                    key: c.field as string,
                    label: c.label,
                    count: filteredItems.reduce((s, r) => s + num(r, c.field), 0),
                })),
            };
        };
        return [
            buildCard('driver', DRIVER_CAUSES),
            buildCard('warehouse', WAREHOUSE_CAUSES),
            buildCard('handover', HANDOVER_CAUSES),
            buildCard('return', RETURN_CAUSES),
        ];
    }, [filteredItems, totalUndelivered]);

    const handleWhsClick = useCallback(
        (record: DeliveryUndeliveredAttributionItem) => {
            const today = new Date().toISOString().split('T')[0];
            const start = timeRange?.startDate ?? today;
            const end = timeRange?.endDate ?? timeRange?.startDate ?? today;
            const params = new URLSearchParams();
            params.set('arrivalDateStart', start);
            params.set('arrivalDateEnd', end);
            params.set('timezone', '1');
            params.set('warehouseIds', String(record.warehouseId));
            navigate(`/wms/delivery/parcel-sla-monitoring?${params.toString()}`);
        },
        [navigate, timeRange?.startDate, timeRange?.endDate],
    );

    const causeColumns = useMemo<ColumnsType<DeliveryUndeliveredAttributionItem>>(() => {
        const buildGroup = (
            categoryKey: CategoryKey,
            causes: SubCauseDef[],
        ): ColumnsType<DeliveryUndeliveredAttributionItem>[number] => ({
            title: (
                <span style={{ color: CATEGORY_STYLE[categoryKey].color, fontWeight: 600 }}>
                    {CATEGORY_STYLE[categoryKey].label}
                </span>
            ),
            children: causes.map((c) => ({
                title: c.label,
                dataIndex: c.field as string,
                key: c.field as string,
                align: 'right' as const,
                width: 120,
                render: (_: unknown, record: DeliveryUndeliveredAttributionItem) => (
                    <NumberCell value={num(record, c.field)} total={attributionTotal(record)} />
                ),
            })),
        });

        return [
            buildGroup('driver', DRIVER_CAUSES),
            buildGroup('warehouse', WAREHOUSE_CAUSES),
            buildGroup('handover', HANDOVER_CAUSES),
            buildGroup('return', RETURN_CAUSES),
        ];
    }, []);

    const columns = useMemo<ColumnsType<DeliveryUndeliveredAttributionItem>>(() => [
        {
            title: 'HUB',
            dataIndex: 'hubId',
            key: 'hub',
            width: 120,
            fixed: 'left' as const,
            render: (hubId: number) => hubIdToLabel.get(Number(hubId)) ?? `Hub#${hubId}`,
        },
        {
            title: 'WHS',
            dataIndex: 'sortingCode',
            key: 'whs',
            width: 200,
            fixed: 'left' as const,
            render: (sortingCode: string | undefined, record: DeliveryUndeliveredAttributionItem) => {
                const label = sortingCode ?? `Warehouse#${record.warehouseId}`;
                return (
                    <button
                        type="button"
                        onClick={() => handleWhsClick(record)}
                        style={{
                            background: 'none', border: 'none', padding: 0,
                            color: '#722ed1', cursor: 'pointer', textAlign: 'left',
                        }}
                    >
                        {label}
                    </button>
                );
            },
        },
        {
            title: '未妥投总数',
            key: 'undeliveredTotal',
            width: 130,
            fixed: 'left' as const,
            align: 'right' as const,
            defaultSortOrder: 'descend' as const,
            sorter: (a, b) => attributionTotal(a) - attributionTotal(b),
            render: (_: unknown, record: DeliveryUndeliveredAttributionItem) => (
                <span style={{ fontWeight: 600 }}>{formatNumber(attributionTotal(record))}</span>
            ),
        },
        ...causeColumns,
        {
            title: '分布',
            key: 'distribution',
            width: 140,
            fixed: 'right' as const,
            render: (_: unknown, record: DeliveryUndeliveredAttributionItem) => <DistributionBar row={record} />,
        },
    ], [causeColumns, handleWhsClick, hubIdToLabel]);

    const [generateDashboardTableExport] = useMutation<
        GenerateDashboardTableExportResponse,
        GenerateDashboardTableExportVariables
    >(GENERATE_DASHBOARD_TABLE_EXPORT);

    const handleDownload = useCallback(async () => {
        if (exportLoading) {
            message.warning('导出任务生成中,请稍候');
            return;
        }
        if (!timeRange?.startDate || !timeRange?.endDate) {
            message.warning('请先选择日期范围');
            return;
        }
        if (sortedRows.length === 0) {
            message.warning('无可导出数据');
            return;
        }
        const headerDefs: Array<[string, (row: DeliveryUndeliveredAttributionItem) => string | number]> = [
            ['HUB', (r) => hubIdToLabel.get(Number(r.hubId)) ?? `Hub#${r.hubId}`],
            ['WHS', (r) => r.sortingCode ?? `Warehouse#${r.warehouseId}`],
            ['未妥投总数', (r) => attributionTotal(r)],
            ['司机侧-合计', (r) => num(r, 'driverSideCount')],
            ...DRIVER_CAUSES.map(
                (c) => [`司机侧-${c.label}`, (r: DeliveryUndeliveredAttributionItem) => num(r, c.field)] as [string, (r: DeliveryUndeliveredAttributionItem) => number],
            ),
            ['仓库侧-合计', (r) => num(r, 'warehouseSideCount')],
            ...WAREHOUSE_CAUSES.map(
                (c) => [`仓库侧-${c.label}`, (r: DeliveryUndeliveredAttributionItem) => num(r, c.field)] as [string, (r: DeliveryUndeliveredAttributionItem) => number],
            ),
            ['交接差异-合计', (r) => num(r, 'handoverDiffCount')],
            ...HANDOVER_CAUSES.map(
                (c) => [`交接差异-${c.label}`, (r: DeliveryUndeliveredAttributionItem) => num(r, c.field)] as [string, (r: DeliveryUndeliveredAttributionItem) => number],
            ),
            ['退件-合计', (r) => num(r, 'returnCount')],
            ...RETURN_CAUSES.map(
                (c) => [`退件-${c.label}`, (r: DeliveryUndeliveredAttributionItem) => num(r, c.field)] as [string, (r: DeliveryUndeliveredAttributionItem) => number],
            ),
        ];
        const headers = headerDefs.map(([h]) => h);
        const mainRows = sortedRows.map((r) => headerDefs.map(([, fn]) => String(fn(r))));

        await runExportWithCooldown(async () => {
            try {
                const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                const auditSummary = `deliveryUndeliveredAttribution ${timeRange.startDate}..${timeRange.endDate} hubId=${selectedHubId}`;
                const response = await generateDashboardTableExport({
                    variables: {
                        input: {
                            fileNamePrefix: 'Delivery_Undelivered_Attribution',
                            timezone: userTimezone,
                            snapshotSheetName: 'Sheet1',
                            mainSheetName: 'Sheet1',
                            snapshotRows: null,
                            mainColumnHeaders: headers,
                            mainRows,
                            auditSummary,
                        },
                    },
                });
                const fileName = response.data?.generateDashboardTableExport;
                if (fileName) {
                    setGeneratedFileName(fileName);
                    setDownloadCompleteModalVisible(true);
                } else {
                    message.error('导出失败,请稍后重试');
                }
            } catch (error: unknown) {
                showError(error);
            }
        });
    }, [exportLoading, timeRange, sortedRows, hubIdToLabel, selectedHubId, generateDashboardTableExport, runExportWithCooldown]);

    return (
        <>
            <div className="mb-4">
                <TimeRangeFilter
                    onChange={setTimeRange}
                    maxDaysBack={365}
                    dashboardTimezone={DASHBOARD_PACIFIC_TZ}
                    presetKeysByQueryType={{
                        [ScaleQueryType.DAILY]: ['yesterday', 'last3days', 'last7days', 'last15days', 'custom'],
                    }}
                />
            </div>

            <div className="mb-4 flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-800 whitespace-nowrap">分拣仓名称</span>
                    <Select
                        style={{ width: 320 }}
                        value={selectedHubId}
                        onChange={setSelectedHubId}
                        options={hubOptions}
                        loading={hubsLoading}
                        placeholder="选择分拣仓"
                    />
                    <span className="font-semibold text-gray-800 whitespace-nowrap">派送站名称</span>
                    <Select
                        style={{ width: 320 }}
                        value={selectedWarehouseId}
                        onChange={setSelectedWarehouseId}
                        options={warehouseOptions}
                        showSearch
                        optionFilterProp="label"
                        placeholder="选择派送站"
                    />
                </div>
                <Button
                    type="primary"
                    onClick={() => void handleDownload()}
                    loading={exportLoading}
                    disabled={exportLoading}
                    className="w-auto min-w-24"
                >
                    <span className="font-bold">{getExportButtonLabel()}</span>
                </Button>
            </div>

            <div className="mb-3 font-semibold text-gray-800">
                未妥投原因分布(按流程图分类)
            </div>
            <div
                className="mb-4"
                style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}
            >
                {categoryCards.map((card) => (
                    <div
                        key={card.key}
                        style={{
                            backgroundColor: card.bgColor,
                            borderRadius: 8,
                            padding: '16px 20px',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                            <span
                                style={{
                                    display: 'inline-block', width: 8, height: 8, borderRadius: 4,
                                    backgroundColor: card.color, marginRight: 8,
                                }}
                            />
                            <span style={{ color: card.color, fontWeight: 600 }}>{card.label}</span>
                        </div>
                        <div style={{ fontSize: 28, fontWeight: 700, color: '#1F1F1F', lineHeight: 1.2 }}>
                            {formatNumber(card.total)}
                        </div>
                        <div style={{ color: '#595959', fontSize: 12, marginTop: 4, marginBottom: 12 }}>
                            占未妥投 {card.ratioText}
                        </div>
                        <div style={{ fontSize: 12, color: '#595959' }}>
                            {card.subs.map((sub) => (
                                <div
                                    key={sub.key}
                                    style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}
                                >
                                    <span>{sub.label}</span>
                                    <span style={{ color: '#262626' }}>{formatNumber(sub.count)}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            <div
                style={{
                    backgroundColor: '#FAFAFA',
                    borderRadius: 6,
                    padding: '10px 14px',
                    marginBottom: 20,
                    fontSize: 12,
                    color: '#595959',
                    lineHeight: 1.6,
                }}
            >
                <span style={{ fontWeight: 600, marginRight: 8 }}>归因逻辑(v2):</span>
                {ATTRIBUTION_LOGIC_TEXT}
            </div>

            <div className="mb-3 flex items-center justify-between">
                <span className="font-semibold text-gray-800">
                    WHS 维度:未妥投原因明细(含各类原因数量及占比)
                </span>
                <span className="text-gray-500 text-xs">
                    未妥投合计:{formatNumber(totalUndelivered)}
                </span>
            </div>

            <Spin spinning={loading}>
                <Table<DeliveryUndeliveredAttributionItem>
                    columns={columns}
                    dataSource={sortedRows}
                    rowKey={(r) => `${r.hubId}-${r.warehouseId}`}
                    pagination={false}
                    bordered
                    size="middle"
                    scroll={{ x: 'max-content' }}
                    sticky
                />
            </Spin>

            <AsyncReportDownloadModal
                visible={downloadCompleteModalVisible}
                onClose={() => setDownloadCompleteModalVisible(false)}
                fileName={generatedFileName}
                reportLabelI18nKey={dashboardAsyncReportLabelI18nKeys.lastMile}
            />
        </>
    );
};

export default UndeliveredAttributionPage;
